/**
 * Replaces {variable_name} template variables in HTML with values from data.
 * Only replaces keys that exist in the data object — CSS braces and unknown
 * variables are left untouched.
 *
 * When data.scan_result_status is present, {scan_result} occurrences that sit
 * in text content (not inside a tag/attribute) are wrapped in
 *   <span class="stc-result">...</span>
 * so they can pick up the status color cascade injected by
 * applyScanResultStyling().
 */
export function interpolateHtml (html, data) {
  if (!html) return ''
  const wrapScanResult = typeof data.scan_result_status === 'string' && !!data.scan_result_status
  return html.replace(/\{([^}]+)\}/g, (match, key, offset, source) => {
    if (!(key in data)) return match
    const value = String(data[key])
    if (key === 'scan_result' && wrapScanResult && !isInsideTag(source, offset)) {
      return `<span class="stc-result">${value}</span>`
    }
    return value
  })
}

/**
 * True when position `index` in `source` sits between an opening `<` and its
 * matching `>` — i.e. inside a start/end tag or attribute list. Good enough
 * heuristic for guarding against wrapping {scan_result} inside attributes.
 */
function isInsideTag (source, index) {
  const lastOpen = source.lastIndexOf('<', index)
  if (lastOpen === -1) return false
  const lastClose = source.lastIndexOf('>', index)
  return lastOpen > lastClose
}

const SCAN_RESULT_STYLE_MARKER = 'data-stc-result-styles'
const SCAN_RESULT_STYLESHEET = `<style ${SCAN_RESULT_STYLE_MARKER}>
[data-scan-result-status="active"]  { --stc-result-color: #65bbef; }
[data-scan-result-status="genuine"] { --stc-result-color: #77c61a; }
[data-scan-result-status="fake"]    { --stc-result-color: #f43838; }
.stc-result { color: var(--stc-result-color); }
</style>`

/**
 * Injects the status hook and stylesheet needed to color-code {scan_result}.
 * - Adds data-scan-result-status="<status>" to the first <body> tag (preserving
 *   existing attributes). If no <body>, wraps the document in a <div> carrying
 *   the attribute.
 * - Inserts the stylesheet just before </head> if present, else at the top.
 * - Idempotent: skips steps whose marker/attribute is already present.
 *
 * `active-unsupported` folds into `active`.
 */
export function applyScanResultStyling (html, status) {
  if (!html) return html
  const normalized = status === 'active-unsupported' ? 'active' : status
  if (!normalized) return html

  let out = html

  if (!/data-scan-result-status\s*=/.test(out)) {
    const bodyMatch = out.match(/<body\b[^>]*>/i)
    if (bodyMatch) {
      const original = bodyMatch[0]
      const replaced = original.replace(/^<body\b/i, `<body data-scan-result-status="${normalized}"`)
      out = out.replace(original, replaced)
    } else {
      out = `<div data-scan-result-status="${normalized}">${out}</div>`
    }
  }

  if (!out.includes(SCAN_RESULT_STYLE_MARKER)) {
    if (/<\/head>/i.test(out)) {
      out = out.replace(/<\/head>/i, `${SCAN_RESULT_STYLESHEET}</head>`)
    } else {
      out = SCAN_RESULT_STYLESHEET + out
    }
  }

  return out
}

export const PRODUCT_IMAGE_PLACEHOLDER = 'https://placehold.co/600x600?text=Product'

export const mockScanData = {
  product_name: 'Sample Product',
  product_description: 'A great product description',
  product_sku: 'SKU-12345',
  product_url: 'https://example.com/product',
  product_image: PRODUCT_IMAGE_PLACEHOLDER,
  brand_name: 'Sample Brand',
  extended_id: 'EXT-001',
  serial_number: 'SN-ABC123',
  scan_count: '5',
  scan_id: 'scan-uuid-001',
  scan_result: 'Genuine',
  scan_result_status: 'genuine',
  country: 'Switzerland',
  api_key: 'demo-api-key'
}
